// @flow
import * as React from 'react'
import AdaptiveSelectField from './AdaptiveSelectField'
import SearchableSelectField from './SearchableSelectField'
import ControlledSearchableSelectField from './ControlledSearchableSelectField'
import FilterSelectField from './FilterSelectField'

// Types
import type { Props as SearchableSelectFieldProps } from './SearchableSelectField'
import type DesktopSelectField from './DesktopSelectField'

export type Props = SearchableSelectFieldProps
export type SelectInnerRefType = DesktopSelectField

// Constants
const defaultSearchableOptions =
  SearchableSelectField.defaultProps.searchableOptions

/**
 * Wrapper around different SelectField implementations which decide
 * what exact implementation to use.
 *
 * It uses just regular static SelectField if no `searchableOptions` or `searchable`
 * prop provided (if you want to render just pretty HTML Select/Option component).
 *
 * If `searchableOptions` provided and it have `requestOptions` field, then it will
 * use a component that will get options using `requestOptions` function and
 * reflect on changes in search query input (get new options using the function).
 * So it is internal search engine and it manage search state using React's state.
 *
 * Otherwise it will render a searchable select input that should be controlled
 * somehow outside, like with Redux or MobX or any other state manager. Then you
 * will need to provide `searchable.query` and `searchable.onQueryChange` fields to
 * controll the options search process.
 *
 * @param  {Object} props
 * @return {Elements}
 */
const SelectField = (props: Props) => {
  const noOptionsProvided = props.searchableOptions === defaultSearchableOptions
  if (!props.searchable && noOptionsProvided) {
    return <AdaptiveSelectField {...props} />
  }
  if (
    props.searchable ||
    props.searchableOptions.filterOptions ||
    props.searchableOptions.rememberInitialOptions
  ) {
    const emptyValue = 'emptyValue' in props ? props.emptyValue : ''
    const finalProps = !props.searchableOptions.filterOptions
      ? { ...props, emptyValue }
      : props
    return <FilterSelectField {...finalProps} />
  }
  if (props.searchableOptions.requestOptions) {
    return <ControlledSearchableSelectField {...props} />
  }
  return <SearchableSelectField {...props} />
}
SelectField.defaultProps = SearchableSelectField.defaultProps

export default SelectField
